/*
 * Copyright (C) 2025 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { CardType } from '@app/app/shared/models';

export enum UserTaskStatus {
  TODO = 'TODO',
  COMPLETED = 'COMPLETED',
  ARCHIVED = 'ARCHIVED',
  OVERDUE = 'OVERDUE',
}

export interface EnrichedTask {
  id: string;
  organizationUid: string;
  type: string;
  title: string;
  contentType: string;
  mandatory: boolean;
  dueDate: string | null;
  createdAt: string;
  updatedAt: string;
  canvasCourseId: number | null;
  canvasAssignmentId: number | null;
  courseName: string | null;
  canvasUrl: string | null;
  status: UserTaskStatus;
  contentDetails?: TaskContentDetails;
  pointsPossible?: number | null;
  submission?: SubmissionDetails | null;
}

export enum SubmissionStatus {
  NOT_SUBMITTED = 'NOT_SUBMITTED',
  SUBMITTED = 'SUBMITTED',
  GRADED = 'GRADED',
  PENDING_REVIEW = 'PENDING_REVIEW',
  EXCUSED = 'EXCUSED',
  MISSING = 'MISSING',
}

export interface ParsedNumber {
  source: string;
  parsedValue: number;
}

export type NumberOrParsed = number | ParsedNumber | null;

export interface SubmissionDetails {
  score: NumberOrParsed;
  grade: string | null;
  pointsPossible: NumberOrParsed;
  percentage: NumberOrParsed;
  submissionStatus: SubmissionStatus;
  submittedAt: string | null;
  gradedAt: string | null;
  late: boolean | null;
  missing: boolean | null;
  excused: boolean | null;
  attempt: number | null;
  assignmentCategory: string | null;
}

export interface TaskContentDetails {
  playlist?: PlaylistSummary;
  card?: CardSummary;
  badge?: BadgeSummary;
}

export interface PlaylistSummary {
  uid: string;
  title: string;
  uri: string;
  thumbnail: string | null;
  timeRequired: number;
  completionCriteria?: CompletionCriteria;
  cards: CardDetail[];
}

export interface CompletionCriteria {
  requirementsMode: string;
  totalRequired: number;
  actions: CompletionAction[];
}

export interface CompletionAction {
  cardId: string;
  cardType: CardType;
  title: string;
  required: boolean;
}

export interface CardSummary {
  uid: string;
  title: string;
  uri: string;
  cardType: string;
  forms: FormDetail[];
}

export interface CardDetail {
  uid: string;
  title: string;
  uri: string;
  cardType: string;
  forms: FormDetail[];
}

export interface FormDetail {
  uid: string;
  title: string;
  formType: FormSectionContentType;
  required: boolean;
}

export interface BadgeSummary {
  title: string;
}

export enum FormSectionContentType {
  CHECKBOX = 'CHECKBOX',
  TEXTBOX = 'TEXTBOX',
  TIME_LOG = 'TIME_LOG',
  USER_FILE_UPLOAD = 'USER_FILE_UPLOAD',
  QUIZ = 'QUIZ',
  COLLECTOR = 'COLLECTOR',
  LIST = 'LIST',
  GROUP_LIST = 'GROUP_LIST',
  SLIDER = 'SLIDER',
  FIRST_NAME = 'FIRST_NAME',
  LAST_NAME = 'LAST_NAME',
  BOOKING = 'BOOKING',
  PRIVATE_NOTE = 'PRIVATE_NOTE',
  CONNECTED_TEXTBOX = 'CONNECTED_TEXTBOX',
  QUESTION_FORM = 'QUESTION_FORM',
  PORTFOLIO = 'PORTFOLIO',
  PROJECT = 'PROJECT',
}
